import { useEffect, useState, useCallback } from "react";
import { Printer, History, FileText, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import {
  previewOwnerLetter,
  saveOwnerLetter,
  listOwnerLetters,
  fetchOwnerLetter,
} from "../lib/api";
import { buildOwnerLetterHtml } from "../lib/ownerLetterHtml";
import { printOwnerLetter } from "../lib/ownerLetterPrint";
import { tenantLocale, tenantTz } from "../lib/company";

/**
 * Owner-notice letter dialog.
 *
 * Live preview reflects the impound's current amount_due (so dispatch can see
 * what the owner will be invoiced today). Hitting "Save & Print" snapshots
 * the letter to the `impound_letters` collection so it's reprintable
 * verbatim later — the snapshot freezes letterhead, owner address, amount
 * due, and due-date as they were at generation time. History tab shows all
 * past generations.
 */
const fmt = (n) => `$${Number(n || 0).toLocaleString(tenantLocale(), { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function OwnerLetterDialog({ jobId, open, onOpenChange }) {
  const [tab, setTab] = useState("preview");
  const [preview, setPreview] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [activeLetter, setActiveLetter] = useState(null); // a saved letter being viewed

  const loadPreview = useCallback(async () => {
    if (!jobId) return;
    setLoading(true);
    try {
      setPreview(await previewOwnerLetter(jobId));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to build letter preview");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  const loadHistory = useCallback(async () => {
    if (!jobId) return;
    try {
      setHistory(await listOwnerLetters(jobId));
    } catch {/* silent */}
  }, [jobId]);

  useEffect(() => {
    if (!open) return;
    setTab("preview");
    setActiveLetter(null);
    loadPreview();
    loadHistory();
  }, [open, loadPreview, loadHistory]);

  /**
   * Render the printable letter into a hidden iframe, then call print() on
   * the iframe's contentWindow. This keeps the print pipeline isolated from
   * the Radix Dialog DOM while not leaving an extra browser tab open.
   */
  const printLetterInNewTab = printOwnerLetter;

  const handleSaveAndPrint = async () => {
    setSaving(true);
    try {
      const saved = await saveOwnerLetter(jobId, {});
      toast.success("Letter saved to impound history");
      setActiveLetter(saved);
      printLetterInNewTab(saved.snapshot);
      loadHistory();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save letter");
    } finally {
      setSaving(false);
    }
  };

  const viewHistoryLetter = async (letterId) => {
    try {
      const doc = await fetchOwnerLetter(letterId);
      setActiveLetter(doc);
      setTab("preview");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load letter");
    }
  };

  // Choose what to render: a saved snapshot if user picked one, otherwise live preview.
  const snapshot = activeLetter ? activeLetter.snapshot : preview;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="owner-letter-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5 text-blue-600" />
            Owner-Notice Letter
          </DialogTitle>
        </DialogHeader>

        {/* Tab switch */}
        <div className="flex gap-2 border-b border-slate-200 dark:border-slate-700 -mt-1 print:hidden">
          <button
            onClick={() => { setTab("preview"); setActiveLetter(null); loadPreview(); }}
            className={`px-3 py-2 text-sm font-semibold border-b-2 ${tab === "preview" && !activeLetter ? "border-blue-600 text-blue-700 dark:text-blue-300" : "border-transparent text-slate-500"}`}
            data-testid="owner-letter-tab-preview"
          >
            Live Preview
          </button>
          <button
            onClick={() => setTab("history")}
            className={`px-3 py-2 text-sm font-semibold border-b-2 flex items-center gap-1.5 ${tab === "history" ? "border-blue-600 text-blue-700 dark:text-blue-300" : "border-transparent text-slate-500"}`}
            data-testid="owner-letter-tab-history"
          >
            <History className="h-4 w-4" />
            History ({history.length})
          </button>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-slate-500 py-6">
            <Loader2 className="h-4 w-4 animate-spin" /> Building preview…
          </div>
        )}

        {tab === "preview" && snapshot && (
          <>
            {activeLetter && (
              <div className="text-[11px] bg-amber-50 dark:bg-amber-950/30 border border-amber-300 dark:border-amber-700 rounded p-2 text-amber-900 dark:text-amber-100 print:hidden">
                Viewing saved letter from <strong>{new Date(activeLetter.created_at).toLocaleString(tenantLocale(), { timeZone: tenantTz(), hourCycle: "h23" })}</strong> by {activeLetter.generated_by_name || "—"}.
                Values shown are the snapshot, not live.
              </div>
            )}
            <iframe
              title="Letter preview"
              srcDoc={buildOwnerLetterHtml(snapshot)}
              className="w-full rounded border border-slate-200 dark:border-slate-700 bg-white"
              style={{ height: "62vh" }}
              data-testid="owner-letter-preview-frame"
            />
            <div className="flex justify-end gap-2 print:hidden border-t border-slate-200 dark:border-slate-700 pt-3">
              {activeLetter ? (
                <Button onClick={() => printLetterInNewTab(snapshot)} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="owner-letter-reprint-btn">
                  <Printer className="h-4 w-4.5" /> Re-print
                </Button>
              ) : (
                <Button onClick={handleSaveAndPrint} disabled={saving} className="bg-slate-900 hover:bg-slate-800 text-white border border-slate-800 dark:bg-transparent dark:border-slate-700 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:border-slate-500 dark:hover:text-white" data-testid="owner-letter-save-print-btn">
                  {saving ? <Loader2 className="h-4 w-4 animate-spin.5" /> : <Save className="h-4 w-4.5" />}
                  Save & Print
                </Button>
              )}
            </div>
          </>
        )}

        {tab === "history" && (
          <div className="space-y-2" data-testid="owner-letter-history-list">
            {history.length === 0 ? (
              <p className="text-sm text-slate-500 py-4 italic">No letters generated yet.</p>
            ) : (
              history.map((l) => (
                <button
                  key={l.id}
                  onClick={() => viewHistoryLetter(l.id)}
                  className="w-full text-left rounded border border-slate-200 dark:border-slate-700 px-3 py-2 hover:bg-slate-50 dark:hover:bg-slate-800"
                  data-testid={`owner-letter-history-row-${l.id}`}
                >
                  <div className="text-sm font-semibold text-slate-800 dark:text-slate-100">
                    {new Date(l.created_at).toLocaleString(tenantLocale(), { timeZone: tenantTz(), dateStyle: "medium", timeStyle: "short", hourCycle: "h23" })}
                  </div>
                  <div className="text-xs text-slate-500">
                    {l.generated_by_name} · Amount: {fmt(l.snapshot?.amount_due)} · Due {l.snapshot?.due_date_label}
                  </div>
                </button>
              ))
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
